import React, { useState, useRef, useContext, useEffect } from 'react';
import { Dialog } from '@mui/material';
import { CmsContext } from '../../context/definitions';

const AddImageModal = ({
  show,
  onHide,
  moduleColor = 'module-primary',
  singleEntryTitle = 'Image',
  singleCategoryTitle = 'Slideshow',
  modeLabel = 'Add',
  replaceMode = false,
  hideCategories = false,
  categories = [],
  categoriesOrderBy = 'title',
  defaultEntryImgAssetId = '',
  entryId = null,
  entry = {},
  entryImgRequired = true,
  imgCategoryId = '',
  onSave,
  onDelete,
  getEntryLogoPreviewUrl,
  getDefaultLogoPreviewUrl,
  generateCmsUrl,
  getEntryLogoName,
}) => {
  const { confirm } = useContext(CmsContext);
  const fileInputRef = useRef(null);

  // State for form
  const [selectedCategory, setSelectedCategory] = useState({ id: entry?.categoryId || '' });
  const [files, setFiles] = useState([{}]); // Mock of ngf-select functionality
  const [imagePreview, setImagePreview] = useState(null);
  const [hasError, setHasError] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Function to handle file selection
  const handleFileSelected = (event) => {
    const file = event.target.files[0];
    if (file && file.type.includes('image')) {
      setFiles([file]);

      // Create preview URL
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Function to check if logo is set
  const logoIsSet = () => {
    return (
      files[0]?.type?.indexOf('image') > -1 || (replaceMode && entry?.img) || defaultEntryImgAssetId
    );
  };

  // Function to reset image
  const resetImg = () => {
    setFiles([{}]);
    setImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  useEffect(() => {
    resetImg();
  }, [entryId]);

  // Function to handle form submission
  const handleSave = async (andClose = false) => {
    setSubmitted(true);

    // Validate form
    if (!hideCategories && !selectedCategory.id) {
      setHasError(true);
      return;
    }

    if (entryImgRequired && !logoIsSet()) {
      setHasError(true);
      return;
    }

    const categoryId = selectedCategory?.id ? selectedCategory.id : imgCategoryId;

    // Prepare data for saving
    const formData = {
      categoryId: categoryId,
      file: files[0]?.type?.indexOf('image') > -1 ? files[0] : null,
      id: entryId,
    };

    // Call onSave function
    await onSave(formData, categoryId, andClose);
    resetImg();
  };

  // Function to handle delete
  const handleDelete = async () => {
    onHide();
    const confirmResponse = await confirm({
      title: 'Delete Image',
      message: `Are you sure you want to delete this image?`,
    });
    if (confirmResponse) {
      onDelete(entryId, imgCategoryId);
    }
  };

  const handleCancel = () => {
    resetImg();
    onHide();
  };

  return (
    <Dialog open={show} onClose={handleCancel} maxWidth="md" fullWidth className="modal-dialog">
      <div className="modal-content">
        <div className={`module ${moduleColor} ng-animate-enabled`} id="entryModal">
          <div className="modal-header module-header">
            <button type="button" className="btn btn-default close" onClick={handleCancel}>
              <i className="fa fa-times"></i>
            </button>
            <span className="modal-title">
              {modeLabel} {singleEntryTitle}
            </span>
          </div>
          <div className="modal-body module-body">
            <div id="entryModalGrowl"></div>
            <form name="entryDialog" role="form">
              {/* Category selection */}
              {!hideCategories && (
                <div className="row no-bottom-margin">
                  <div className="col-sm-12">
                    <div className="form-horizontal">
                      <div
                        className={`form-group ${
                          hasError && submitted && !selectedCategory.id ? 'has-error' : ''
                        }`}
                      >
                        <label htmlFor="category" className="col-sm-3 control-label">
                          {singleCategoryTitle} *
                        </label>
                        <div className="col-sm-9">
                          <select
                            className="form-control"
                            name="category"
                            id="category"
                            value={selectedCategory.id}
                            onChange={(e) => setSelectedCategory({ id: e.target.value })}
                            required
                          >
                            <option value="" disabled>
                              Select a {singleCategoryTitle}...
                            </option>
                            {categories
                              .sort((a, b) => a[categoriesOrderBy] - b[categoriesOrderBy])
                              .map((category) => (
                                <option key={category.id} value={category.id}>
                                  {category.title}
                                </option>
                              ))}
                          </select>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Image upload section */}
              <div
                className={`row content ${hideCategories ? '' : 'border-top'} ${
                  files[0]?.type?.indexOf('image') > -1 || replaceMode ? 'no-bottom-padding' : ''
                } ${!logoIsSet() && submitted && entryImgRequired ? 'has-error' : ''}`}
              >
                <div className="col-sm-3">
                  <label
                    className={`control-label ${entryImgRequired ? 'required' : ''}`}
                    htmlFor="entryLogo"
                  >
                    {singleEntryTitle}
                  </label>
                </div>
                <div className="col-sm-9">
                  <span id="logo-help-block" className="help-block small">
                    Please select an {singleEntryTitle.toLowerCase()}.
                  </span>
                </div>
                <div className="col-sm-12">
                  <div className="form-inline">
                    <div className="row no-bottom-margin">
                      {/* Image preview section */}
                      {files[0]?.type?.indexOf('image') > -1 && (
                        <div className="image-container col-sm-4 col-sm-6 padding-bottom">
                          <div className="remove-img-container" onClick={resetImg}>
                            <img className="full-width" src={imagePreview} alt="Preview" />
                          </div>
                        </div>
                      )}

                      {/* Existing image display */}
                      {!(files[0]?.type?.indexOf('image') > -1) &&
                        (replaceMode || defaultEntryImgAssetId) && (
                          <div className="image-container col-sm-4 col-sm-6 padding-bottom">
                            {generateCmsUrl &&
                            getEntryLogoName &&
                            entry?.id &&
                            generateCmsUrl(getEntryLogoName(entry.id)) === entry.img ? (
                              <div className="remove-img-container" onClick={resetImg}>
                                <img
                                  className="full-width"
                                  src={getEntryLogoPreviewUrl(entryId)}
                                  alt="Entry logo"
                                />
                              </div>
                            ) : (
                              <img
                                className="full-width"
                                src={getDefaultLogoPreviewUrl(defaultEntryImgAssetId)}
                                alt="Default logo"
                              />
                            )}
                          </div>
                        )}

                      {/* File upload button */}
                      <div
                        className={`col-sm-8 col-sm-12 no-left-padding ${
                          files[0]?.type?.indexOf('image') > -1 || replaceMode
                            ? 'padding-bottom'
                            : ''
                        }`}
                      >
                        <input
                          ref={fileInputRef}
                          type="file"
                          name="entryLogo"
                          id="entryLogo"
                          accept="image/*"
                          onChange={handleFileSelected}
                          style={{ display: 'none' }}
                        />
                        <button
                          type="button"
                          className="btn"
                          onClick={() => fileInputRef.current.click()}
                        >
                          Choose File
                        </button>
                        <span className="font-12px margin-left">
                          (File should be no larger than 400 width x 400 height in pixels)
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </form>
          </div>

          {/* Modal footer with action buttons */}
          <div className="modal-footer">
            <div className="text-center" style={{ marginLeft: '-1px' }}>
              {replaceMode && (
                <button type="button" onClick={handleDelete} className="btn">
                  Delete
                </button>
              )}

              <button type="button" className="btn btn-primary" onClick={() => handleSave(true)}>
                Save and Close
              </button>

              {!replaceMode && (
                <button type="button" className="btn btn-primary" onClick={handleCancel}>
                  Cancel
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </Dialog>
  );
};

export default AddImageModal;
